import { ImageOff, RefreshCw, X } from 'lucide-react-native';
import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  type GestureResponderEvent,
  type ImageStyle,
  type StyleProp,
  type ViewStyle,
  View,
} from 'react-native';

import { LoadableImage } from '@/src/components/LoadableImage';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { ZoomablePhoto } from './ZoomablePhoto';

type PhotoSourceStage = 'thumbnail' | 'original';
export type PhotoContext =
  | 'post'
  | 'comment'
  | 'garage_vehicle'
  | 'profile_avatar'
  | 'profile_cover';

type ReliablePhotoProps = {
  accessibilityLabel: string;
  analyticsContext: PhotoContext;
  contentFit: 'contain' | 'cover';
  imageStyle: StyleProp<ImageStyle>;
  originalURL: string;
  preferOriginal?: boolean;
  thumbnailURL?: string | null;
};

type PhotoAttachmentProps = ReliablePhotoProps & {
  frameStyle: StyleProp<ViewStyle>;
  viewerTitle: string;
};

function cleanURL(value: string | null | undefined) {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}

function initialStage({
  preferOriginal,
  thumbnailURL,
}: {
  preferOriginal: boolean;
  thumbnailURL: string | null;
}): PhotoSourceStage {
  if (preferOriginal || !thumbnailURL) {
    return 'original';
  }

  return 'thumbnail';
}

function ReliablePhoto({
  accessibilityLabel,
  analyticsContext,
  contentFit,
  imageStyle,
  originalURL,
  preferOriginal = false,
  thumbnailURL,
}: ReliablePhotoProps) {
  const originalSourceURL = cleanURL(originalURL);
  const thumbnailSourceURL = cleanURL(thumbnailURL);
  const distinctThumbnailURL =
    thumbnailSourceURL && thumbnailSourceURL !== originalSourceURL
      ? thumbnailSourceURL
      : null;
  const resetKey = `${originalSourceURL ?? ''}|${distinctThumbnailURL ?? ''}|${preferOriginal ? 'original' : 'thumbnail'}`;
  const [stage, setStage] = useState<PhotoSourceStage>(() =>
    initialStage({
      preferOriginal,
      thumbnailURL: distinctThumbnailURL,
    }),
  );
  const [failed, setFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setStage(
      initialStage({
        preferOriginal,
        thumbnailURL: distinctThumbnailURL,
      }),
    );
    setFailed(false);
    setRetryCount(0);
  }, [distinctThumbnailURL, preferOriginal, resetKey]);

  const sourceURL = useMemo(() => {
    if (stage === 'thumbnail') {
      return distinctThumbnailURL ?? originalSourceURL;
    }

    return originalSourceURL ?? distinctThumbnailURL;
  }, [distinctThumbnailURL, originalSourceURL, stage]);

  function retry(event?: GestureResponderEvent) {
    event?.stopPropagation();
    setStage(
      initialStage({
        preferOriginal,
        thumbnailURL: distinctThumbnailURL,
      }),
    );
    setFailed(false);
    setRetryCount((current) => current + 1);
  }

  function handleError() {
    trackEvent('media_image_load_error', {
      fallback_available:
        stage === 'thumbnail'
          ? Boolean(originalSourceURL)
          : Boolean(distinctThumbnailURL),
      has_thumbnail: Boolean(distinctThumbnailURL),
      media_context: analyticsContext,
      source_type: stage,
    });

    if (stage === 'thumbnail' && originalSourceURL) {
      setStage('original');
      return;
    }

    if (stage === 'original' && distinctThumbnailURL) {
      setStage('thumbnail');
      return;
    }

    setFailed(true);
  }

  if (!sourceURL || failed) {
    return (
      <Pressable
        accessibilityLabel="Retry loading photo"
        accessibilityRole="button"
        onPress={retry}
        style={[imageStyle, styles.failure]}
      >
        <ImageOff color={colors.mutedText} size={26} strokeWidth={2.2} />
        <View style={styles.failureCopy}>
          <Text style={styles.failureTitle}>Photo unavailable</Text>
          <View style={styles.retryRow}>
            <RefreshCw color={colors.secondary} size={15} strokeWidth={2.4} />
            <Text style={styles.retryText}>Tap to retry</Text>
          </View>
        </View>
      </Pressable>
    );
  }

  return (
    <LoadableImage
      key={`${sourceURL}-${retryCount}`}
      accessibilityLabel={accessibilityLabel}
      cachePolicy="memory-disk"
      contentFit={contentFit}
      onError={handleError}
      source={{ uri: sourceURL }}
      style={imageStyle}
      transition={120}
    />
  );
}

export function PhotoViewer({
  accessibilityLabel,
  analyticsContext,
  onClose,
  originalURL,
  thumbnailURL,
  title,
  visible,
}: {
  accessibilityLabel: string;
  analyticsContext: PhotoContext;
  onClose: () => void;
  originalURL: string;
  thumbnailURL?: string | null;
  title: string;
  visible: boolean;
}) {
  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      !visible ||
      typeof window === 'undefined'
    ) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, visible]);

  return (
    <Modal
      animationType="fade"
      onRequestClose={onClose}
      transparent
      visible={visible}
    >
      <Pressable
        accessibilityLabel="Close photo viewer"
        accessibilityRole="button"
        onPress={onClose}
        style={styles.viewerBackdrop}
      >
        <Pressable
          accessibilityLabel={title}
          onPress={(event) => event.stopPropagation()}
          style={styles.viewerContent}
        >
          <ZoomablePhoto
            accessibilityLabel={accessibilityLabel}
            loaderColor={colors.surface}
            sourceURL={cleanURL(originalURL) ?? cleanURL(thumbnailURL) ?? ''}
            style={styles.viewerImage}
          />
        </Pressable>
        <Pressable
          accessibilityLabel="Close photo viewer"
          accessibilityRole="button"
          onPress={onClose}
          style={({ pressed }) => [
            styles.closeButton,
            pressed ? styles.closeButtonPressed : null,
          ]}
        >
          <X color={colors.surface} size={24} strokeWidth={2.5} />
        </Pressable>
      </Pressable>
    </Modal>
  );
}

export function PhotoAttachment({
  accessibilityLabel,
  analyticsContext,
  contentFit,
  frameStyle,
  imageStyle,
  originalURL,
  thumbnailURL,
  viewerTitle,
}: PhotoAttachmentProps) {
  const [viewerVisible, setViewerVisible] = useState(false);

  function openViewer(event: GestureResponderEvent) {
    event.stopPropagation();
    trackEvent('media_photo_view_open', {
      has_thumbnail: Boolean(cleanURL(thumbnailURL)),
      media_context: analyticsContext,
    });
    setViewerVisible(true);
  }

  return (
    <>
      <Pressable
        accessibilityLabel={`Open ${viewerTitle}`}
        accessibilityRole="button"
        onPress={openViewer}
        style={({ pressed }) => [
          frameStyle,
          pressed ? styles.attachmentPressed : null,
        ]}
      >
        <ReliablePhoto
          accessibilityLabel={accessibilityLabel}
          analyticsContext={analyticsContext}
          contentFit={contentFit}
          imageStyle={imageStyle}
          originalURL={originalURL}
          thumbnailURL={thumbnailURL}
        />
      </Pressable>
      <PhotoViewer
        accessibilityLabel={accessibilityLabel}
        analyticsContext={analyticsContext}
        onClose={() => setViewerVisible(false)}
        originalURL={originalURL}
        thumbnailURL={thumbnailURL}
        title={viewerTitle}
        visible={viewerVisible}
      />
    </>
  );
}

const styles = StyleSheet.create({
  attachmentPressed: {
    opacity: 0.92,
  },
  closeButton: {
    alignItems: 'center',
    backgroundColor: 'rgba(13, 9, 43, 0.72)',
    borderRadius: 22,
    height: 44,
    justifyContent: 'center',
    position: 'absolute',
    right: spacing.lg,
    top: spacing.xl,
    width: 44,
  },
  closeButtonPressed: {
    opacity: 0.78,
  },
  failure: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    gap: spacing.sm,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  failureCopy: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  failureTitle: {
    color: colors.mutedText,
    fontSize: 14,
    fontWeight: '800',
    textAlign: 'center',
  },
  retryRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
  },
  retryText: {
    color: colors.secondary,
    fontSize: 13,
    fontWeight: '900',
  },
  viewerBackdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(13, 9, 43, 0.94)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.md,
  },
  viewerContent: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  viewerImage: {
    height: '100%',
    width: '100%',
  },
});
